import { createContext, useContext, useEffect, useState, useRef } from 'react';
import { GeneratedHistory, PushHistory } from '@Models/types';
import { useTestCaseContext } from './TestCaseContext';
import showToast from '@Common/toast/showToast';
import { useAuth } from './CognitoAuthProvider';
import { testCasesAPI } from '@Lib/axios/testcases';

type QueueItem = GeneratedHistory | PushHistory;

export type QueueContextType = {
  queue: QueueItem[];
  queueStatus: string;
  setQueueStatus: (status: string) => void;
  addToQueue: (id: string, isPushHistory: boolean) => Promise<void>;
  setQueue: (queue: QueueItem[]) => void;
  removeFromQueue: (id: string) => void;
  isInProgress: (item: QueueItem) => boolean;
};

export const QueueContext = createContext<QueueContextType | null>(null);

interface Props {
  children: React.ReactNode;
}

export const QueueContextProvider = ({ children }: Props) => {
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [queueStatus, setQueueStatus] = useState('No Pending Tasks');
  const isCheckingStatus = useRef(false);

  const { logout } = useAuth();
  const testcases = testCasesAPI(logout);

  const { fetchGeneratedHistory, fetchPublishHistory } = useTestCaseContext();

  useEffect(() => {
    if (queue.some((q) => isInProgress(q))) {
      setQueueStatus('In Progress');
      const id = setInterval(() => {
        if (!isCheckingStatus.current) {
          checkStatus();
        }
      }, 5000);
      return () => clearInterval(id);
    } else {
      if (queue.length > 0) {
        setQueueStatus('Completed');
        showToast.success('All tasks completed', 'generate-success');
      } else {
        setQueueStatus('No Pending Tasks');
      }
    }
  }, [queue]);

  const isInProgress = (item: QueueItem) => {
    if ('completion_status' in item) {
      return item.completion_status === 'in_progress';
    }
    if ('push_status' in item) {
      return item.push_status === 'in_progress';
    }
    return false;
  };

  const status = async (
    id: string,
    isPushHistory: boolean
  ): Promise<QueueItem> => {
    if (isPushHistory) {
      // Use getPushStatus for PushHistory
      const res = await testcases.getPushStatus(id);
      return res as PushHistory;
    } else {
      // Use getStatus for GeneratedHistory
      const res = await testcases.getStatus(id);
      return res as GeneratedHistory;
    }
  };

  const checkStatus = async () => {
    try {
      const updatedQueue = await Promise.all(
        queue.map(async (item) => {
          const isPush = 'push_status' in item;
          const updatedItem = await status(item.id, isPush);
          if ('push_status' in updatedItem) {
            if (updatedItem.push_status === 'completed') {
              fetchPublishHistory();
            }
          } else {
            if (updatedItem.completion_status === 'completed') {
              fetchGeneratedHistory();
            }
          }
          return updatedItem;
        })
      );

      const isCompleted = updatedQueue.every((item) => !isInProgress(item));
      setQueue(updatedQueue);

      if (isCompleted) {
        setQueueStatus('Completed');
      }
    } catch (error) {
      console.error('Error checking status:', error);
    }
  };

  const addToQueue = async (id: string, isPushHistory: boolean) => {
    try {
      setQueueStatus('In Progress');
      if (!queue.some((q) => q.id === id)) {
        const queuedItem = await status(id, isPushHistory);
        setQueue((prevQueue) => [...prevQueue, queuedItem]);
      }
    } catch (error) {
      console.error('Error adding to queue:', error);
    }
  };

  const removeFromQueue = (id: string) => {
    setQueue((prevQueue) => prevQueue.filter((q) => q.id !== id));
  };

  const contextValue = {
    queue,
    queueStatus,
    setQueue,
    setQueueStatus,
    addToQueue,
    removeFromQueue,
    isInProgress,
  };

  return (
    <QueueContext.Provider value={contextValue}>
      {children}
    </QueueContext.Provider>
  );
};

export const useQueueContext = (): QueueContextType => {
  const context = useContext(QueueContext);
  if (!context)
    throw new Error(
      'useQueueContext must be used within a QueueContextProvider'
    );
  return context;
};
